import React from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Languages, Menu, X, MessageCircle, Instagram, Facebook, MapPin, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import AIChatWidget from "@/components/chat/AIChatWidget";
import FloatingActions from "@/components/layout/FloatingActions";
import CurrencyToggle from "@/components/layout/CurrencyToggle";
import ScrollProgressThread from "@/components/layout/ScrollProgressThread";
import DataSourceModal from "@/components/common/DataSourceModal";
import { pushDL } from "@/lib/analytics";
import { withLang } from "@/lib/langUrl";
import { sendClick } from "@/lib/tracking";

// Height of the fixed header (mobile h-24 = 96px, desktop lg:h-28 = 112px).
// We offset scroll so anchor targets aren't hidden beneath the sticky bar.
const HEADER_OFFSET_MOBILE = 96;
const HEADER_OFFSET_DESKTOP = 112;

function scrollToId(id, behavior = "smooth") {
  const el = document.getElementById(id);
  if (!el) return false;
  const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
  const offset = isDesktop ? HEADER_OFFSET_DESKTOP : HEADER_OFFSET_MOBILE;
  const top = el.getBoundingClientRect().top + window.scrollY - offset - 8;
  window.scrollTo({ top, behavior });
  return true;
}

export default function PublicLayout() {
  const { t, lang, lh, isRTL } = useLang();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  // Scroll to the hash target every time the route/hash changes. Handles both
  // same-page navigation and cross-page arrivals like /insights → /#market.
  // Framer-motion `whileInView` animations above the target mount as we
  // scroll, shifting layout heights and yanking the target away. To counter
  // that, we do an initial instant scroll, then two correction passes after
  // animations settle (150ms + 900ms), then a final smooth "polish" pass.
  React.useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    let tries = 0;
    const findAndScroll = (behavior) => {
      const found = scrollToId(id, behavior);
      if (found) return true;
      if (tries > 30) return false;
      tries += 1;
      setTimeout(() => findAndScroll(behavior), 80);
      return false;
    };
    // 1st pass: instant snap so user lands roughly at the target immediately
    findAndScroll("instant");
    // 2nd pass: correction after framer-motion whileInView animations mount
    setTimeout(() => scrollToId(id, "instant"), 250);
    // 3rd pass: final smooth polish after all above-fold animations settle
    setTimeout(() => scrollToId(id, "smooth"), 1000);
  }, [location.pathname, location.hash]);

  const handleNav = (e, to) => {
    setOpen(false);
    if (!to.includes("#")) return; // Let router handle plain routes normally
    e.preventDefault();
    const [path, hash] = to.split("#");
    const targetPath = path || "/";
    if (location.pathname === targetPath) {
      scrollToId(hash);
      // Update the URL hash without triggering a full re-navigation.
      window.history.replaceState(null, "", lh(`${targetPath}#${hash}`));
    } else {
      navigate(lh(`${targetPath}#${hash}`));
    }
  };

  // Language toggle = navigate to the same route in the other language (real URL).
  const toggle = () => {
    const next = lang === "en" ? "ar" : "en";
    const cur = `${location.pathname}${location.search}${location.hash}`;
    navigate(withLang(cur, next), { preventScrollReset: true });
  };

  const links = [
    { to: "/", key: "home" },
    { to: "/property-deal-analyzer", key: "deal_analyzer" },
    { to: "/dubai-market", key: "market" },
    { to: "/dubai-compare-areas", key: "compare" },
    { to: "/aiagent", key: "aiagent" },
    { to: "/mortgage-calculator", key: "calculator" },
    { to: "/insights", key: "insights" },
    { to: "/about", key: "about" },
  ];

  return (
    <div className="relative min-h-screen text-propertic-ink overflow-x-hidden">
      {/* Floating header */}
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled
            ? "bg-propertic-paper/98 backdrop-blur-xl border-b hairline shadow-sm"
            : "bg-propertic-paper/92 backdrop-blur-md border-b border-propertic-ink/5"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-4 sm:px-10 h-24 sm:h-24 lg:h-28 flex items-center justify-between gap-2 sm:gap-8">
          <Link to={lh("/")} data-testid="header-logo" className="flex items-center gap-2 group shrink-0">
            <img
              src="/propertic-logo.png"
              alt="The Propertic"
              data-testid="header-logo-img"
              width="240"
              height="240"
              className="h-20 sm:h-20 lg:h-24 w-auto object-contain drop-shadow-sm"
              draggable={false}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={lh(l.to)}
                end={l.to === "/"}
                onClick={(e) => handleNav(e, l.to)}
                data-testid={`nav-${l.key}-link`}
                className={({ isActive }) => `link-nav text-[13px] whitespace-nowrap ${isActive ? "active" : ""}`}
              >
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <CurrencyToggle />
            <button
              type="button"
              onClick={toggle}
              data-testid="lang-toggle"
              aria-label={lang === "ar" ? "تغيير اللغة" : "Switch language"}
              className="group/lang relative h-9 sm:h-10 px-2.5 sm:px-3.5 font-mono-brand text-propertic-ink hover:text-propertic-navy rounded-full border hairline hover:border-propertic-gold transition-all inline-flex items-center gap-1.5"
            >
              <img
                src={`https://flagcdn.com/w40/${lang === "en" ? "ae" : "gb"}.png`}
                srcSet={`https://flagcdn.com/w80/${lang === "en" ? "ae" : "gb"}.png 2x`}
                alt={lang === "en" ? "UAE flag" : "UK flag"}
                width="20"
                height="15"
                className="w-5 h-auto rounded-[2px] object-cover shadow-sm"
                draggable={false}
              />
              <span className="text-[10px] sm:text-xs tracking-[0.14em]">
                {lang === "en" ? "AR" : "EN"}
              </span>
              {/* Hover-preview, quick peek at how the hero H1 will look
                  post-toggle. Desktop only, doesn't fire on mobile taps. */}
              <span
                aria-hidden="true"
                className="hidden lg:block pointer-events-none absolute top-full end-0 mt-2 px-3 py-2 rounded-xl bg-propertic-navy text-propertic-paper text-[11px] font-editorial shadow-2xl whitespace-nowrap opacity-0 -translate-y-1 group-hover/lang:opacity-100 group-hover/lang:translate-y-0 transition-all duration-200 z-40"
                dir={lang === "en" ? "rtl" : "ltr"}
              >
                {lang === "en" ? "عقارات دبي. أوضح، وأكثر أماناً." : "Dubai real estate. Simplified, and Secured."}
              </span>
            </button>
            <a
              href={lh("/#consultation")}
              onClick={(e) => handleNav(e, "/#consultation")}
              data-testid="desktop-consultation-cta"
              className="hidden lg:inline-flex items-center h-10 px-5 rounded-full bg-propertic-navy text-propertic-paper hover:bg-propertic-navy/90 hover:shadow-lg text-xs font-mono-brand tracking-[0.14em] uppercase transition-all"
            >
              {t("nav.consultation")}
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden h-9 w-9 sm:h-10 sm:w-10 grid place-items-center rounded-full border hairline hover:border-propertic-gold transition-colors"
            >
              {open ? <X className="w-3.5 h-3.5" /> : <Menu className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden bg-propertic-paper border-t hairline">
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.key}
                  to={lh(l.to)}
                  end={l.to === "/"}
                  onClick={(e) => handleNav(e, l.to)}
                  data-testid={`nav-mobile-${l.key}-link`}
                  className="text-base text-propertic-ink font-editorial"
                >
                  {t(`nav.${l.key}`)}
                </NavLink>
              ))}
              <a
                href={lh("/#consultation")}
                data-testid="mobile-consultation-cta"
                onClick={(e) => handleNav(e, "/#consultation")}
                className="btn-primary rounded-full px-6 h-11 grid place-items-center text-xs font-mono-brand mt-2"
              >
                {t("nav.consultation")}
              </a>
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10 pt-24 sm:pt-24 lg:pt-28">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="relative overflow-hidden bg-propertic-navy text-propertic-paper mt-16">
        {/* Curved top edge */}
        <div className="curve-divider absolute -top-1 inset-x-0 text-propertic-paper" aria-hidden="true">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path fill="currentColor" d="M0,60 C480,0 960,0 1440,60 L1440,0 L0,0 Z" />
          </svg>
        </div>
        <div className="blob-gold w-[500px] h-[500px] -top-40 -end-40" />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-8 pt-24 pb-14 grid gap-14 md:grid-cols-12 relative">
          <div className="md:col-span-5">
            <p className="text-sm text-propertic-paper/70 max-w-md leading-relaxed">{t("footer.tagline")}</p>
            <p className="mt-6 font-mono-brand text-propertic-gold-2/90">RERA #43950 · Provest Real Estate LLC</p>

            <div className="mt-8 flex items-center gap-3">
              <a href="https://www.instagram.com/thepropertic" target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full grid place-items-center border border-propertic-paper/20 hover:bg-propertic-gold hover:border-propertic-gold transition-all" data-testid="footer-instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://www.facebook.com/share/19iriY9AdV/" target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full grid place-items-center border border-propertic-paper/20 hover:bg-propertic-gold hover:border-propertic-gold transition-all" data-testid="footer-facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://wa.me/971563003311" target="_blank" rel="noreferrer" onClick={() => { sendClick("whatsapp"); pushDL("whatsapp_click", { source: "footer_social" }); }} className="w-11 h-11 rounded-full grid place-items-center border border-propertic-paper/20 hover:bg-propertic-gold hover:border-propertic-gold transition-all" data-testid="footer-whatsapp">
                <MessageCircle className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono-brand text-propertic-gold-2/90 mb-5">{lang === "ar" ? "استكشف" : "Explore"}</p>
            <ul className="space-y-3 text-sm text-propertic-paper/85">
              <li><Link to={lh("/")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-home-link">{t("nav.home")}</Link></li>
              <li><Link to={lh("/property-deal-analyzer")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-deal-analyzer-link">{lang === "ar" ? "محلّل صفقات العقار" : "Property Deal Analyzer"}</Link></li>
              <li><Link to={lh("/dubai-market")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-dubai-market-link">{lang === "ar" ? "سوق دبي" : "Dubai Market"}</Link></li>
              <li><Link to={lh("/dubai-compare-areas")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-compare-areas-link">{lang === "ar" ? "قارن المناطق" : "Compare Areas"}</Link></li>
              <li><Link to={lh("/aiagent")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-aiagent-link">{lang === "ar" ? "روني · المساعد الذكي" : "Roni · AI Advisor"}</Link></li>
              <li><Link to={lh("/mortgage-calculator")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-mortgage-calc-link">{lang === "ar" ? "حاسبة الرهن" : "Mortgage Calculator"}</Link></li>
              <li><Link to={lh("/insights")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-insights-link">{lang === "ar" ? "التحليلات الأسبوعية" : "Weekly Insights"}</Link></li>
              <li><Link to={lh("/about")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-about-link">{t("footer.about_us")}</Link></li>
              <li><Link to={lh("/uae-mortgage")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-uae-mortgage-link">{lang === "ar" ? "الرهن العقاري في الإمارات" : "UAE Mortgage Guide"}</Link></li>
              <li><Link to={lh("/dubai-recently-delivered")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-recently-delivered-link">{lang === "ar" ? "مشاريع تم تسليمها" : "Recently Delivered"}</Link></li>
              <li><Link to="/admin/login" className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-admin-link">{t("nav.admin")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono-brand text-propertic-gold-2/90 mb-5">{t("footer.contact")}</p>
            <ul className="space-y-4 text-sm text-propertic-paper/85">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-propertic-gold-2 flex-shrink-0" />
                <span>{lang === "ar" ? "البرشاء الأولى، دبي، الإمارات العربية المتحدة" : "Al Barsha 1, Dubai, United Arab Emirates"}</span>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="w-4 h-4 text-propertic-gold-2" />
                <a dir="ltr" href="https://wa.me/971563003311" onClick={() => { sendClick("whatsapp"); pushDL("whatsapp_click", { source: "footer_contact" }); }} className="hover:text-propertic-gold-2 transition-colors">+971 56 300 3311</a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-propertic-gold-2" />
                <a dir="ltr" href="tel:+971563003311" onClick={() => { sendClick("call"); pushDL("phone_click", { source: "footer_contact" }); }} className="hover:text-propertic-gold-2 transition-colors">+971 56 300 3311</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-propertic-paper/10">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-propertic-paper/50">
            <p>© {new Date().getFullYear()} The Propertic by Provest. {t("footer.rights")}</p>
            <div className="flex items-center gap-5 font-mono-brand">
              <DataSourceModal />
              <span className="opacity-30">·</span>
              <Link to={lh("/terms")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-terms-link">{t("footer.terms")}</Link>
              <span className="opacity-30">·</span>
              <Link to={lh("/privacy")} className="hover:text-propertic-gold-2 transition-colors" data-testid="footer-privacy-link">{t("footer.privacy")}</Link>
            </div>
          </div>
        </div>
      </footer>

      <FloatingActions isRTL={isRTL} />
      <AIChatWidget />
      <ScrollProgressThread />
    </div>
  );
}
